import { MdOutlinePublic, MdOutlineLock } from 'react-icons/md';
import { FiUsers, FiUser, FiFileText, FiHelpCircle, FiEdit3 } from 'react-icons/fi';
import { BsMegaphone, BsSuitClub } from 'react-icons/bs';

/** Reserved category for status posts not shared to a club. */
export const PERSONAL_STATUS_CATEGORY_KEY = '__personal__';

/** UI tabs: Story, Article, Event */
export const POST_COMPOSE_MODES = ['status', 'club', 'event'];

export const DEFAULT_POST_COMPOSE_MODE = 'club';

export const POST_COMPOSE_CONTENT_TYPES = ['story', 'question', 'article', 'announcement'];

export const DEFAULT_POST_COMPOSE_CONTENT_TYPE = 'story';

export function normalizePostComposeMode(mode) {
  const m = String(mode || '').trim().toLowerCase();
  if (m === 'story') return 'status';
  if (m === 'articles' || m === 'article') return 'club';
  return POST_COMPOSE_MODES.includes(m) ? m : DEFAULT_POST_COMPOSE_MODE;
}

export function isArticleLikeComposeMode(mode) {
  return normalizePostComposeMode(mode) === 'club';
}

export function normalizePostComposeContentType(type) {
  const t = String(type || '').trim().toLowerCase();
  return POST_COMPOSE_CONTENT_TYPES.includes(t) ? t : DEFAULT_POST_COMPOSE_CONTENT_TYPE;
}

export function isStoryComposeContentType(type) {
  return normalizePostComposeContentType(type) === 'story';
}

export function isArticleLikeComposeContentType(type) {
  return !isStoryComposeContentType(type);
}

/** True when publishing to the user's wall rather than a club. */
export function isWallComposePublishTarget(newCat, articleClubSlugs = [], publishTarget = null) {
  if (publishTarget === 'club') return false;
  if ((articleClubSlugs || []).some((s) => String(s || '').trim())) return false;
  const newCatSlug = String(newCat || '').trim();
  if (newCatSlug && newCatSlug !== PERSONAL_STATUS_CATEGORY_KEY) return false;
  return publishTarget === 'story' || !newCatSlug || newCatSlug === PERSONAL_STATUS_CATEGORY_KEY;
}

/** Wall posts use personal category on the server; content type is postContentType. */
export function isWallComposePost(composeContentType, newCat, articleClubSlugs = [], publishTarget = null) {
  if (isStoryComposeContentType(composeContentType)) {
    return publishTarget !== 'club';
  }
  return isWallComposePublishTarget(newCat, articleClubSlugs, publishTarget);
}

/** Primary club slug when saving a compose payload (wall → __personal__). */
export function resolveComposePrimaryClubSlug({
  composeContentType,
  newCat,
  articleClubSlugs = [],
  composePublishTarget = 'story',
  statusShareClubs = [],
}) {
  const isStoryContent = isStoryComposeContentType(composeContentType);
  const isWallPost = isWallComposePost(composeContentType, newCat, articleClubSlugs, composePublishTarget);
  if (isWallPost) return PERSONAL_STATUS_CATEGORY_KEY;

  if (isStoryContent) {
    const storyShareClubs = resolveStoryComposeShareClubSlugs(
      statusShareClubs,
      composePublishTarget,
      articleClubSlugs
    );
    return storyShareClubs[0] || String(newCat || '').trim();
  }

  const articleClubs = (articleClubSlugs || []).filter((slug) => String(slug || '').trim());
  return articleClubs[0] || String(newCat || '').trim();
}

/** Additional club slugs when saving (primary club is categoryKey / clubSlug). */
export function resolveComposeShareClubSlugs({
  composeContentType,
  articleClubSlugs = [],
  composePublishTarget = 'story',
  statusShareClubs = [],
}) {
  const isStoryContent = isStoryComposeContentType(composeContentType);
  if (isStoryContent) {
    const storyShareClubs = resolveStoryComposeShareClubSlugs(
      statusShareClubs,
      composePublishTarget,
      articleClubSlugs
    );
    if (composePublishTarget === 'club') {
      return storyShareClubs.slice(1);
    }
    return storyShareClubs;
  }
  const articleClubs = (articleClubSlugs || []).filter((slug) => String(slug || '').trim());
  return articleClubs.slice(1);
}

/** Story posts shared to clubs — from share picker or club publish target. */
export function resolveStoryComposeShareClubSlugs(
  statusShareClubs = [],
  composePublishTarget = 'story',
  articleClubSlugs = []
) {
  const fromPicker = (statusShareClubs || [])
    .map((slug) => String(slug || '').trim())
    .filter((slug) => slug && slug !== PERSONAL_STATUS_CATEGORY_KEY);
  if (fromPicker.length) return fromPicker;
  if (composePublishTarget === 'club') {
    return (articleClubSlugs || [])
      .map((slug) => String(slug || '').trim())
      .filter((slug) => slug && slug !== PERSONAL_STATUS_CATEGORY_KEY);
  }
  return [];
}

export function composeModeForContentType(type) {
  return isStoryComposeContentType(type) ? 'status' : 'club';
}

export const POST_COMPOSE_CONTENT_TYPE_OPTIONS = [
  {
    value: 'story',
    shortLabel: 'Story',
    label: 'Story',
    description: 'Share a quick update on your wall.',
    iconTone: 'story',
    icon: <FiEdit3 aria-hidden />,
  },
  {
    value: 'question',
    shortLabel: 'Question',
    label: 'Question',
    description: 'Ask the community for help or ideas.',
    iconTone: 'question',
    icon: <FiHelpCircle aria-hidden />,
  },
  {
    value: 'article',
    shortLabel: 'Article',
    label: 'Article',
    description: 'Write a longer post with a title and details.',
    iconTone: 'article',
    icon: <FiFileText aria-hidden />,
  },
  {
    value: 'announcement',
    shortLabel: 'Announcement',
    label: 'Announcement',
    description: 'Share important news with your audience.',
    iconTone: 'announcement',
    icon: <BsMegaphone aria-hidden />,
  },
];

/** Feed filter rows — keep in sync with compose content types (add/remove types in POST_COMPOSE_CONTENT_TYPE_OPTIONS only). */
export function feedPostContentTypeFilterOptions() {
  return POST_COMPOSE_CONTENT_TYPE_OPTIONS.map(({ value, label, shortLabel, icon, iconTone }) => ({
    value,
    label: shortLabel || label,
    icon,
    iconTone,
  }));
}

export function composeContentTypeShortLabel(type) {
  const normalized = normalizePostComposeContentType(type);
  return POST_COMPOSE_CONTENT_TYPE_OPTIONS.find((opt) => opt.value === normalized)?.shortLabel || 'Story';
}

export function composeContentTypeOption(type) {
  const normalized = normalizePostComposeContentType(type);
  return POST_COMPOSE_CONTENT_TYPE_OPTIONS.find((opt) => opt.value === normalized) || POST_COMPOSE_CONTENT_TYPE_OPTIONS[0];
}

export const POST_COMPOSE_PUBLISH_TARGET_OPTIONS = [
  {
    value: 'story',
    label: 'My wall',
    headlineLabel: 'My wall',
    description: 'Share on your wall and choose who can see it.',
    iconTone: 'wall',
    icon: <FiUser aria-hidden />,
  },
  {
    value: 'club',
    label: 'Club',
    headlineLabel: 'My club',
    description: 'Publish to one or more clubs you belong to.',
    iconTone: 'club',
    icon: <BsSuitClub className="support-open-disc-dropdown-club-icon" aria-hidden />,
  },
];

export function composePublishTargetHeadlineLabel(target, clubSummary = '') {
  if (target === 'club') {
    const summary = String(clubSummary || '').trim();
    if (summary && summary !== 'Choose clubs') {
      if (/\bclubs?\b/i.test(summary)) return summary;
      if (summary.includes(' and ') || summary.includes(',')) return `${summary} clubs`;
      return `${summary} club`;
    }
    return 'my club';
  }
  return 'my wall';
}

/** Oxford-style list: "A, B, and C" */
export function formatClubNameList(labels) {
  const names = (labels || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function clubHeadlineDisplayLabel(clubName) {
  const name = String(clubName || '').trim();
  if (!name) return 'my club';
  if (/\bclubs?$/i.test(name)) return name;
  return `${name} club`;
}

/**
 * Club headline destination — inline name for 1 club; compact count + tooltip for 2+.
 * @returns {{ preposition: 'on'|'to', label: string, tooltip: string, compact: boolean }}
 */
export function composeClubHeadlineDestination(clubLabels) {
  const labels = (clubLabels || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!labels.length) {
    return { preposition: 'on', label: 'my club', tooltip: '', compact: false };
  }
  if (labels.length === 1) {
    return {
      preposition: 'on',
      label: clubHeadlineDisplayLabel(labels[0]),
      tooltip: labels[0],
      compact: false,
    };
  }
  const count = labels.length;
  return {
    preposition: 'to',
    label: `${count} clubs`,
    tooltip: formatClubNameList(labels),
    compact: true,
  };
}

/**
 * Club count for the compose “Where?” line — 1 club / N clubs with hover list.
 * @returns {{ label: string, tooltip: string, compact: boolean }}
 */
export function composeClubHeadlineWhereTarget(clubLabels) {
  const labels = (clubLabels || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!labels.length) {
    return { label: 'Choose clubs', tooltip: '', compact: false };
  }
  const count = labels.length;
  return {
    label: count === 1 ? '1 club' : `${count} clubs`,
    tooltip: formatClubNameList(labels),
    compact: true,
  };
}

export function composeStoryAudienceHeadlineLabel(audience, audiencePeopleCount, audiencePeopleLabel) {
  if (audience === 'public') return 'Public';
  if (audience === 'followers') return 'Followers';
  if (audience === 'only_me') return 'Only me';
  if (audience === 'selected') {
    return audiencePeopleCount > 0 ? audiencePeopleLabel : 'selected people';
  }
  if (audience === 'hide_selected') {
    return audiencePeopleCount > 0 ? audiencePeopleLabel : 'selected people';
  }
  return 'Followers';
}

/** Lowercase audience phrase for compose destination summary (sentence case). */
export function composeStoryAudienceDestinationLabel(audience, audiencePeopleCount, audiencePeopleLabel) {
  return composeStoryAudienceHeadlineLabel(audience, audiencePeopleCount, audiencePeopleLabel).toLowerCase();
}

export const POST_COMPOSE_TAB_OPTIONS = [
  { value: 'status', label: 'Story' },
  { value: 'club', label: 'Article' },
  { value: 'event', label: 'Event' },
];

export const STATUS_AUDIENCE_OPTIONS = [
  { value: 'public', label: 'Public', iconTone: 'public', icon: <MdOutlinePublic aria-hidden /> },
  { value: 'followers', label: 'Followers', iconTone: 'followers', icon: <FiUsers aria-hidden /> },
  { value: 'selected', label: 'Selected people', iconTone: 'selected', icon: <FiUser aria-hidden /> },
  {
    value: 'hide_selected',
    label: 'Do not show to selected people',
    iconTone: 'hide-selected',
    icon: <FiUsers aria-hidden />,
  },
  { value: 'only_me', label: 'Only me', iconTone: 'only-me', icon: <MdOutlineLock aria-hidden /> },
];

export const STATUS_AUDIENCE_NEEDS_USERS = new Set(['selected', 'hide_selected']);

export function audienceOptionForValue(value) {
  return STATUS_AUDIENCE_OPTIONS.find((opt) => opt.value === value) || null;
}

export function audienceIconToneClass(tone, baseClass = '') {
  const toneClass = tone ? `support-open-disc-audience-icon--${tone}` : '';
  return [baseClass, toneClass].filter(Boolean).join(' ');
}

export function contentTypeIconToneClass(tone, baseClass = '') {
  const toneClass = tone ? `support-open-disc-content-type-icon--${tone}` : '';
  return [baseClass, toneClass].filter(Boolean).join(' ');
}

export const CLUB_PUBLISH_ICON_TONE = 'club';

export function clubIconToneClass(baseClass = '') {
  return audienceIconToneClass(CLUB_PUBLISH_ICON_TONE, baseClass);
}

export function publishTargetIconToneClass(tone, baseClass = '') {
  return audienceIconToneClass(tone, baseClass);
}

/** Compose headline club icon row — desktop shows up to this many before a 10+ badge. */
export const COMPOSE_HEADLINE_CLUB_ICON_DESKTOP_MAX = 10;
export const COMPOSE_HEADLINE_CLUB_ICON_DESKTOP_MIN_WIDTH = 961;

/** Create post shell — child compose modals must not render smaller than this. */
export const CREATE_POST_MODAL_WIDTH = 'min(720px, calc(100vw - 48px))';
export const CREATE_POST_MODAL_MAX_HEIGHT = 'min(88vh, 760px)';
/** Edit post — taller cap; modal grows with content before scrolling. */
export const CREATE_POST_EDIT_MODAL_MAX_HEIGHT = 'min(92vh, 920px)';
/** Stable centered edit modal — height from CSS so mobile can use full viewport. */
export const CREATE_POST_EDIT_MODAL_CONTENT_STYLE = {
  maxHeight: CREATE_POST_EDIT_MODAL_MAX_HEIGHT,
  boxSizing: 'border-box',
};
export const CREATE_POST_EDIT_MODAL_CLASS = 'support-open-disc-create-post-modal--edit';
export const CREATE_POST_EDIT_MODAL_ANCHORED_CLASS = 'support-open-disc-create-post-modal--edit-anchored';
export const CREATE_POST_EDIT_MODAL_OVERLAY_ANCHORED_CLASS = 'support-open-disc-compose-edit-modal-overlay--anchored';
export const CREATE_POST_COMPOSE_TOOL_MODAL_LARGE_WIDTH = 'min(920px, calc(100vw - 40px))';
export const CREATE_POST_MODAL_SELECTOR = '.support-open-disc-create-post-modal.modal-content';
/** Stacked above edit-post modal overlays (desktop 3010 / mobile 1000005). */
export const EDIT_POST_CLOSE_CONFIRM_MODAL_CLASS = 'support-open-disc-edit-close-confirm-modal';

/** CenterModalWindow close control — shared by compose tool modals (e.g. content type picker). */
export const POST_COMPOSE_MODAL_CLOSE_BUTTON_PROPS = {
  closeButtonTooltip: 'Escape to close',
};

/** localStorage key prefix — per-user dismiss for create-post setup coachmark. */
export const COMPOSE_SETUP_HINT_STORAGE_KEY = 'od_compose_setup_hint_seen_v1';
